import axios, { AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import type { AxiosResponse } from 'axios';
import { useViewerStore } from '@/stores/viewer/viewer-store';
import {
  applyValidatedExplicitOrganizationHeaders,
  clearOrganizationHeaders,
  deleteHeader,
  getHeaderValue,
  getValidatedOrganizationById,
  getValidatedOrganizationBySlug,
  getValidatedOrganizationContextHeaderValue,
  hasExplicitOrganizationHeaders,
  setHeader,
} from './client';
import { handle401WithSilentRefresh } from './authRefresh';
import { getCurrentAuthTokens } from './auth-session';

const isObjectIdLike = (value: string | undefined): value is string =>
  typeof value === 'string' && /^[a-f0-9]{24}$/i.test(value);

const isUnscopedDataServiceRequest = (url?: string): boolean => {
  const path = url?.split('?')[0];
  return path === '/health';
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const hasQueryExecutionContext = (data: unknown): boolean => {
  if (!isRecord(data) || !isRecord(data.queryContext)) {
    return false;
  }

  return typeof data.queryContext.requestingOrganizationId === 'string' &&
    data.queryContext.requestingOrganizationId.trim().length > 0;
};

const isExplicitDataOwnerQueryRequest = (
  config: InternalAxiosRequestConfig,
): boolean => {
  const path = config.url?.split('?')[0];
  return (
    config.method?.toLowerCase() === 'post' &&
    path === '/api/query' &&
    hasHeader(config.headers, 'X-Organization-Id') &&
    hasQueryExecutionContext(config.data)
  );
};

export type DataServiceListingScope = 'current' | 'platform';

/**
 * Endpoints whose response is platform-scoped (cross-org listing for
 * platform admins) and which the data-service correctly filters server-side
 * based on the JWT's isPlatformAdmin claim. For these endpoints, the
 * client-side org-context pre-flight is relaxed when the caller is a
 * platform admin — the request is sent without org headers and the
 * data-service's schemaService.listSchemas (and equivalents) read
 * isPlatformAdmin from the verified token and return all-org results.
 *
 * Non-admin users hitting the same endpoint without an org context still
 * fail the pre-flight check (defense in depth). The server's
 * schemaService also returns an empty result for non-admin requests with
 * no org filter — see schemaService.listSchemas line ~218.
 *
 * Keep this list narrow: only endpoints that are explicitly platform-
 * scoped admin listings. Org-scoped reads/writes (creates, updates,
 * deletes, data queries) still REQUIRE an org context.
 */
const PLATFORM_LISTING_PATHS = new Set<string>([
  '/api/schemas',
  '/api/groups',
]);

const getRequestPath = (url?: string): string | null => {
  if (!url) return null;
  return url.split('?')[0] || null;
};

const getQueryScope = (url?: string): string | null => {
  const query = url?.split('?')[1];
  if (!query) return null;
  return new URLSearchParams(query).get('scope');
};

const getParamsScope = (params: unknown): string | null => {
  if (params instanceof URLSearchParams) {
    return params.get('scope');
  }

  if (params !== null && typeof params === 'object' && !Array.isArray(params)) {
    const scope = Reflect.get(params, 'scope');
    return typeof scope === 'string' ? scope : null;
  }

  return null;
};

const isExplicitPlatformListingRequest = (
  config: InternalAxiosRequestConfig,
): boolean => {
  const path = getRequestPath(config.url);
  if (!path) return false;
  // Exact match only — '/api/schemas/<id>' is NOT a listing, it's a fetch
  // for a specific schema that still needs org context.
  if (!PLATFORM_LISTING_PATHS.has(path)) return false;
  return (
    getParamsScope(config.params) === 'platform' ||
    getQueryScope(config.url) === 'platform'
  );
};

const setDataServiceOrganizationHeaders = (
  config: InternalAxiosRequestConfig,
  organization: { _id: string; slug: string },
): void => {
  setHeader(config.headers, 'X-Organization-Context', organization.slug);
  if (isObjectIdLike(organization._id)) {
    setHeader(config.headers, 'X-Organization-Id', organization._id);
  } else {
    deleteHeader(config.headers, 'X-Organization-Id');
  }
};

const getValidatedDataServiceOrganization = async (): Promise<{
  _id: string;
  slug: string;
} | null> => {
  const organizationSlug = await getValidatedOrganizationContextHeaderValue();
  const organizationFromContext = organizationSlug
    ? await getValidatedOrganizationBySlug(organizationSlug)
    : null;

  const viewerOrgId = useViewerStore.getState().viewerOrgId;
  if (!viewerOrgId) {
    return organizationFromContext;
  }

  const viewerOrganization = await getValidatedOrganizationById(viewerOrgId);
  if (viewerOrganization) {
    return viewerOrganization;
  }

  return organizationFromContext;
};

export const getValidatedDataServiceOrganizationId = async (): Promise<string | null> => {
  const organization = await getValidatedDataServiceOrganization();
  if (isObjectIdLike(organization?._id)) {
    return organization._id;
  }

  return null;
};

export const applyDataServiceOrganizationHeaders = async (
  config: InternalAxiosRequestConfig,
  options: { allowUnscopedHealthCheck: boolean },
): Promise<InternalAxiosRequestConfig> => {
  const isExplicitPlatformListing = isExplicitPlatformListingRequest(config);
  const hasPerRequestOrgOverride =
    hasExplicitOrganizationHeaders(config.headers);

  if (hasPerRequestOrgOverride) {
    if (isExplicitDataOwnerQueryRequest(config)) {
      deleteHeader(config.headers, 'X-Organization-Context');
    } else {
      await applyValidatedExplicitOrganizationHeaders(config.headers);
    }
  } else if (!isExplicitPlatformListing) {
    const organization = await getValidatedDataServiceOrganization();
    if (organization) {
      setDataServiceOrganizationHeaders(config, organization);
    }
  }

  const scopedOrganizationId = getHeaderValue(
    config.headers,
    'X-Organization-Id',
  );
  const hasOrganizationContext =
    scopedOrganizationId !== null ||
    getHeaderValue(config.headers, 'X-Organization-Context') !== null;

  if (
    !hasOrganizationContext &&
    !(options.allowUnscopedHealthCheck && isUnscopedDataServiceRequest(config.url)) &&
    !isExplicitPlatformListing
  ) {
    throw new Error('Organization context required for data-service request');
  }

  if (scopedOrganizationId) {
    setHeader(config.headers, 'x-tenant-id', scopedOrganizationId);
  } else if (!scopedOrganizationId) {
    deleteHeader(config.headers, 'x-tenant-id');
  }

  return config;
};

export interface DataServiceClientOptions {
  /** Base URL for the data service instance */
  baseURL: string;
  /** Request timeout in milliseconds (default: 30000) */
  timeout?: number;
}

export interface DataOwnerQueryRequest {
  groupId: string;
  schemaId: string;
  dataOwnerOrganizationId: string;
  requestingOrganizationId: string;
  filter?: Record<string, unknown>;
  sort?: Record<string, 1 | -1>;
  skip?: number;
  limit?: number;
  projection?: Record<string, 0 | 1>;
  schemaVersion?: number;
}

export const executeDataOwnerQuery = async (
  client: AxiosInstance,
  request: DataOwnerQueryRequest,
): Promise<AxiosResponse<unknown>> => {
  const {
    dataOwnerOrganizationId,
    requestingOrganizationId,
    ...query
  } = request;

  return client.post(
    '/api/query',
    {
      ...query,
      queryContext: {
        requestingOrganizationId,
      },
    },
    {
      headers: {
        'X-Organization-Id': dataOwnerOrganizationId,
      },
    }
  );
};

/**
 * Creates an axios instance configured for TerraMatrix data-service endpoints.
 * Handles auth token, org context, viewer org override, and tenant ID headers.
 * Used by schemaApi, groupApi, queryTemplateApi, and dataApi.
 * NOTE: serverApi uses a different pattern (custom 401 re-auth interceptor) — do not use this factory there.
 * NOTE: binaryFormatApi is NOT a consumer — it uses native fetch and targets the workflow service, not the data service.
 */
export function createDataServiceClient({
  baseURL,
  timeout = 30000,
}: DataServiceClientOptions): AxiosInstance {
  const client = axios.create({
    baseURL,
    timeout,
    headers: { 'Content-Type': 'application/json' },
  });

  client.interceptors.request.use(
    async (config) => {
      const tokens = getCurrentAuthTokens();

      if (tokens?.accessToken) {
        config.headers.Authorization = `Bearer ${tokens.accessToken}`;
      }

      return applyDataServiceOrganizationHeaders(config, { allowUnscopedHealthCheck: true });
    },
    (error) => Promise.reject(error),
  );

  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      // 401: delegate to the shared silent-refresh-then-modal handler so a
      // revoked-jti 401 from data-service recovers exactly the same way as
      // one from main-backend or agent-service. Before this, data-service
      // 401s were normalized into an ApiError and silently bubbled up to
      // the caller (showing "Failed to load schemas" instead of triggering
      // re-auth) — see authRefresh.ts header for the full rationale.
      if (originalRequest && error.response?.status === 401 && !originalRequest._retry) {
        const hadToken = getCurrentAuthTokens();
        if (hadToken?.accessToken) {
          originalRequest._retry = true;
          try {
            return await handle401WithSilentRefresh(error, client);
          } catch {
            // Refresh-then-modal exhausted (user dismissed, refresh hard
            // failed, etc.) — fall through to the normalized ApiError below
            // so the caller still gets a structured rejection.
          }
        }
      }

      const apiError = {
        code: error.response?.data?.code || error.response?.data?.error || 'UNKNOWN_ERROR',
        message: error.response?.data?.message || error.message || 'An unexpected error occurred',
        details: error.response?.data?.details,
        status: error.response?.status,
      };
      return Promise.reject(apiError);
    },
  );

  return client;
}
